import { createFileRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { listPublishedPosts } from "@/lib/blog.functions";

export const Route = createFileRoute("/blog")({
  head: () => ({
    meta: [
      { title: "Blog — thatKorner" },
      {
        name: "description",
        content:
          "Field notes, product updates, and editorial from thatKorner — the native AI dining concierge.",
      },
      { property: "og:title", content: "Blog — thatKorner" },
      { property: "og:description", content: "Editorial, field notes, and updates from thatKorner." },
      { property: "og:url", content: "https://www.thatkorner.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://www.thatkorner.com/blog" }],
  }),
  component: BlogLayout,
});

function BlogLayout() {
  const matches = useMatches();
  const isIndex = matches[matches.length - 1]?.routeId === "/blog";
  if (!isIndex) return <Outlet />;
  return <BlogIndex />;
}

function BlogIndex() {
  const fetchPosts = useServerFn(listPublishedPosts);
  const { data, isLoading } = useQuery({
    queryKey: ["blog", "published"],
    queryFn: () => fetchPosts(),
  });

  const posts = data?.posts ?? [];

  return (
    <section className="bg-ink pb-24 pt-32 text-bone">
      <div className="mx-auto max-w-content px-gutter">
        <h1 className="h-display text-5xl md:text-6xl text-center">Blog</h1>



        <div className="mt-14">
          {isLoading ? (
            <p className="text-bone/70">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="text-bone/60">No posts yet. Check back soon.</p>
          ) : (
            <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group block overflow-hidden rounded-2xl border border-bone/10 bg-bone/[0.02] transition-colors hover:border-ember/40"
                >
                  {p.cover_image_url && (
                    <div className="aspect-[16/10] w-full overflow-hidden bg-bone/5">
                      <img
                        src={p.cover_image_url}
                        alt={p.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {p.tags?.length ? (
                      <div className="mb-3 flex flex-wrap gap-1.5">
                        {p.tags.slice(0, 3).map((t: string) => (
                          <span
                            key={t}
                            className="rounded-full border border-bone/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-bone/60"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <h2 className="h-display text-2xl text-bone group-hover:text-ember">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-2 text-sm text-bone/65 line-clamp-3">{p.excerpt}</p>
                    )}
                    {p.published_at && (
                      <p className="mt-4 text-xs text-bone/70">
                        {new Date(p.published_at).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
